import { useEffect, useMemo, useState } from 'react';
import { Info, Pencil, Plus } from 'lucide-react';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { formatInt } from '../../lib/format';
import {
  GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT,
  GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT,
  usePlatformPricingTiers,
} from '../../api/admin';
import type {
  PlatformPricingTier,
  PlatformPricingTierSlug,
} from '../../mocks/admin';

const SLUG_RE = /^tier_[a-z0-9_]+$/;

/* ── Slug regex helper ───────────────────────────────────────────────────── */
function isValidSlug(s: string): boolean {
  return SLUG_RE.test(s);
}

/** Renders 5 / 15 / 10k or "unlimited" — capability hint cell. */
function HintCells({
  locations,
  users,
  products,
}: {
  locations: number | null;
  users: number | null;
  products: number | null;
}) {
  const allUnlimited = locations === null && users === null && products === null;
  if (allUnlimited) {
    return (
      <span className="text-xs text-zinc-500 dark:text-zinc-400 italic">unlimited</span>
    );
  }
  const fmt = (v: number | null): string => {
    if (v === null) return '∞';
    if (v >= 1000) {
      // 10000 → 10k, 1500 → 1.5k
      const k = v / 1000;
      return Number.isInteger(k) ? `${k}k` : `${k.toFixed(1)}k`;
    }
    return formatInt(v);
  };
  return (
    <span className="text-xs text-zinc-600 dark:text-zinc-300 mono whitespace-nowrap">
      {fmt(locations)} / {fmt(users)} / {fmt(products)}
    </span>
  );
}

/** Active / Inactive pill for tier rows. */
function TierStatusBadge({ active }: { active: boolean }) {
  if (active) return <Badge tone="success">Active</Badge>;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap bg-zinc-100 text-zinc-500 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700">
      <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 dark:bg-zinc-500" />
      Inactive
    </span>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

type DrawerMode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; tier: PlatformPricingTier };

export function PricingTiers() {
  const { data: platformPricingTiers, isLoading } = usePlatformPricingTiers();
  const [tiers, setTiers] = useState<PlatformPricingTier[]>([]);
  const [drawer, setDrawer] = useState<DrawerMode>({ kind: 'closed' });

  // Seed local (editable) tier state once the fetched tiers land. Keyed on the
  // raw query data (stable `undefined` until loaded) so this runs once.
  useEffect(() => {
    if (!platformPricingTiers) return;
    setTiers([...platformPricingTiers].sort((a, b) => a.sortOrder - b.sortOrder));
  }, [platformPricingTiers]);

  function upsertTier(t: PlatformPricingTier) {
    setTiers((prev) => {
      const idx = prev.findIndex((x) => x.id === t.id);
      if (idx === -1) return [...prev, t].sort((a, b) => a.sortOrder - b.sortOrder);
      const next = [...prev];
      next[idx] = t;
      return next.sort((a, b) => a.sortOrder - b.sortOrder);
    });
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title={<span>Pricing tiers {isLoading ? <Skeleton className="inline-block w-12 h-5 rounded align-middle" /> : <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">({tiers.length})</span>}</span>}
        subtitle="Minimum monthly + yearly prices that reseller packages cannot go below."
        action={
          <Button
            iconLeft={<Plus className="w-4 h-4" />}
            onClick={() => setDrawer({ kind: 'create' })}
          >
            New tier
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Slug</th>
                <th className="text-left font-medium px-4 py-3">Display name</th>
                <th className="text-right font-medium px-4 py-3">Min monthly</th>
                <th className="text-right font-medium px-4 py-3">Min yearly</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">
                  Hints
                  <span className="ml-1 normal-case font-normal text-[10px] text-zinc-400 dark:text-zinc-500">
                    (loc / users / products)
                  </span>
                </th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">Packages</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3"><Skeleton className="h-4 w-24 rounded" /></td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-28 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-40 max-w-full rounded" />
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded ml-auto" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-24 rounded" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-8 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : tiers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No pricing tiers yet. Create one to set reseller price floors.
                  </td>
                </tr>
              ) : (
                tiers.map((t) => (
                <tr
                  key={t.id}
                  className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition"
                >
                  <td className="px-5 sm:px-6 py-3">
                    <span className="mono text-xs text-zinc-700 dark:text-zinc-200">
                      {t.slug}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-zinc-950 dark:text-zinc-50">
                      {t.displayName}
                    </div>
                    {t.description && (
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 max-w-xs truncate">
                        {t.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Money
                      minor={t.defaultMinMonthlyMinor}
                      hideCents
                      tone="neutral"
                      className="text-zinc-950 dark:text-zinc-50 font-medium"
                    />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Money
                      minor={t.defaultMinYearlyMinor}
                      hideCents
                      tone="neutral"
                      className="text-zinc-950 dark:text-zinc-50 font-medium"
                    />
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <HintCells
                      locations={t.hintMaxLocations}
                      users={t.hintMaxUsers}
                      products={t.hintMaxProducts}
                    />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden md:table-cell">
                    {t.packageCount}
                  </td>
                  <td className="px-4 py-3">
                    <TierStatusBadge active={t.isActive} />
                  </td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setDrawer({ kind: 'edit', tier: t })}
                      aria-label={`Edit ${t.displayName}`}
                      className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4">
        <Banner tone="info" title="How floors apply">
          Existing packages stay where they are until the reseller next edits them. The new
          floor only kicks in on next save. Global absolute floor:{' '}
          <span className="mono whitespace-nowrap">
            ৳{formatInt(GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT)}/month
          </span>
          ,{' '}
          <span className="mono whitespace-nowrap">
            ৳{formatInt(GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT)}/year
          </span>
          .
        </Banner>
      </div>

      {drawer.kind !== 'closed' && (
        <TierDrawer
          mode={drawer}
          onClose={() => setDrawer({ kind: 'closed' })}
          onSave={(t) => {
            upsertTier(t);
            setDrawer({ kind: 'closed' });
          }}
        />
      )}
    </div>
  );
}

/* ── Drawer ─────────────────────────────────────────────────────────────── */

function TierDrawer({
  mode,
  onClose,
  onSave,
}: {
  mode: { kind: 'create' } | { kind: 'edit'; tier: PlatformPricingTier };
  onClose: () => void;
  onSave: (t: PlatformPricingTier) => void;
}) {
  const editing = mode.kind === 'edit';
  const seed = editing ? mode.tier : null;

  const [slug, setSlug] = useState<string>(seed?.slug ?? 'tier_');
  const [displayName, setDisplayName] = useState(seed?.displayName ?? '');
  const [description, setDescription] = useState(seed?.description ?? '');
  const [minMonthly, setMinMonthly] = useState<string>(
    seed ? String(seed.defaultMinMonthlyMinor) : '',
  );
  const [minYearly, setMinYearly] = useState<string>(
    seed ? String(seed.defaultMinYearlyMinor) : '',
  );
  const [maxLocations, setMaxLocations] = useState<string>(
    seed?.hintMaxLocations === null || seed?.hintMaxLocations === undefined
      ? ''
      : String(seed.hintMaxLocations),
  );
  const [maxUsers, setMaxUsers] = useState<string>(
    seed?.hintMaxUsers === null || seed?.hintMaxUsers === undefined
      ? ''
      : String(seed.hintMaxUsers),
  );
  const [maxProducts, setMaxProducts] = useState<string>(
    seed?.hintMaxProducts === null || seed?.hintMaxProducts === undefined
      ? ''
      : String(seed.hintMaxProducts),
  );
  const [isActive, setIsActive] = useState<boolean>(seed?.isActive ?? true);
  const [submitting, setSubmitting] = useState(false);

  const monthlyNum = Number(minMonthly);
  const yearlyNum = Number(minYearly);

  const errors = useMemo(() => {
    const e: Partial<{
      slug: string;
      displayName: string;
      minMonthly: string;
      minYearly: string;
    }> = {};
    if (!editing && !isValidSlug(slug)) {
      e.slug = 'Slug must match tier_[a-z0-9_]+';
    }
    if (displayName.trim().length === 0) {
      e.displayName = 'Display name is required';
    }
    if (!Number.isFinite(monthlyNum) || monthlyNum < GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT) {
      e.minMonthly = `Must be at least ৳${GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT}`;
    }
    if (!Number.isFinite(yearlyNum) || yearlyNum < GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT) {
      e.minYearly = `Must be at least ৳${GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT}`;
    }
    return e;
  }, [editing, slug, displayName, monthlyNum, yearlyNum]);

  const yearlyShapeWarning =
    Number.isFinite(monthlyNum) &&
    Number.isFinite(yearlyNum) &&
    monthlyNum > 0 &&
    yearlyNum > monthlyNum * 12;

  const valid = Object.keys(errors).length === 0;

  function handleSave() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setTimeout(() => {
      const next: PlatformPricingTier = {
        id: seed?.id ?? `pt_${slug.replace(/^tier_/, '')}`,
        slug: (editing ? seed!.slug : (slug as PlatformPricingTierSlug)),
        displayName: displayName.trim(),
        description: description.trim(),
        defaultMinMonthlyMinor: monthlyNum,
        defaultMinYearlyMinor: yearlyNum,
        hintMaxLocations: maxLocations.trim() === '' ? null : Number(maxLocations),
        hintMaxUsers: maxUsers.trim() === '' ? null : Number(maxUsers),
        hintMaxProducts: maxProducts.trim() === '' ? null : Number(maxProducts),
        isActive,
        sortOrder: seed?.sortOrder ?? 99,
        packageCount: seed?.packageCount ?? 0,
      };
      setSubmitting(false);
      onSave(next);
    }, 700);
  }

  const subscriberPackageCount = seed?.packageCount ?? 0;
  const showSubscribersWarning = editing && subscriberPackageCount > 0;

  return (
    <Drawer
      open
      onClose={onClose}
      title={editing ? `Edit tier · ${seed!.displayName}` : 'New pricing tier'}
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={submitting} disabled={!valid}>
            {editing ? 'Save' : 'Create tier'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Input
          label="Slug"
          required
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          disabled={editing}
          hint={
            editing
              ? 'Slug cannot be changed after creation.'
              : 'Lowercase, prefixed with tier_ (e.g. tier_starter).'
          }
          error={errors.slug}
          className="mono"
        />

        <Input
          label="Display name"
          required
          placeholder="Starter · Basic · Pro · Enterprise"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          error={errors.displayName}
        />

        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
            Description
          </label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Single location, very small business"
            className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 placeholder-zinc-400 dark:placeholder-zinc-500 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm focus:border-brand dark:focus:border-brand-dm resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Min monthly (BDT)"
            required
            type="number"
            min={GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT}
            value={minMonthly}
            onChange={(e) => setMinMonthly(e.target.value)}
            prefix={<span className="text-sm">৳</span>}
            error={errors.minMonthly}
            hint={
              !errors.minMonthly
                ? `Global floor: ৳${formatInt(GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT)}`
                : undefined
            }
          />
          <Input
            label="Min yearly (BDT)"
            required
            type="number"
            min={GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT}
            value={minYearly}
            onChange={(e) => setMinYearly(e.target.value)}
            prefix={<span className="text-sm">৳</span>}
            error={errors.minYearly}
            hint={
              !errors.minYearly
                ? `Global floor: ৳${formatInt(GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT)}`
                : undefined
            }
          />
        </div>

        {yearlyShapeWarning && !errors.minMonthly && !errors.minYearly && (
          <Banner tone="warning">
            Yearly is more than 12× monthly; that&apos;s an unusual discount shape.
          </Banner>
        )}

        <fieldset className="border-t border-zinc-200 dark:border-zinc-700 pt-5">
          <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">
            Capability hints
            <span className="ml-1.5 text-xs font-normal text-zinc-500 dark:text-zinc-400">
              (blank = unlimited)
            </span>
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              label="Max locations"
              type="number"
              min={0}
              placeholder="∞"
              value={maxLocations}
              onChange={(e) => setMaxLocations(e.target.value)}
            />
            <Input
              label="Max users"
              type="number"
              min={0}
              placeholder="∞"
              value={maxUsers}
              onChange={(e) => setMaxUsers(e.target.value)}
            />
            <Input
              label="Max products"
              type="number"
              min={0}
              placeholder="∞"
              value={maxProducts}
              onChange={(e) => setMaxProducts(e.target.value)}
            />
          </div>
          <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
            Hints are advisory — they help resellers pick the right tier when creating
            packages. Hard limits live on the package itself.
          </p>
        </fieldset>

        <fieldset className="border-t border-zinc-200 dark:border-zinc-700 pt-5">
          <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Availability
          </legend>
          <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
            />
            <span>
              <strong>Active</strong> — resellers can pick this tier when creating a
              package.
            </span>
          </label>
        </fieldset>

        {showSubscribersWarning && (
          <Banner tone="warning" title="Heads up">
            This change affects floor for new package saves only. {subscriberPackageCount}{' '}
            existing reseller{' '}
            {subscriberPackageCount === 1 ? 'package is' : 'packages are'} on this tier and
            stay grandfathered until next edit.
          </Banner>
        )}

        {!editing && (
          <Banner tone="info" className="text-xs">
            <span className="inline-flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5 inline-block" />
              Once created, the slug is immutable — pick carefully.
            </span>
          </Banner>
        )}
      </div>
    </Drawer>
  );
}
